import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { fetchAssessmentTypes, fetchFieldSets } from "../../../services/httpClient";
import useApp from "../../../utils/useApp";
import SaveChangesModal from "../../ui/SaveChangesModal";
import { AssessmentType } from "../../../types/assessmenttype";
import AssessmentCard from "./AssessmentCard";
import CreateAssessmentCard from "./CreateAssessmentCard";
import { unsavedChanges } from "../../../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullhorn, faEdit } from "@fortawesome/free-solid-svg-icons";
import AssessmentModal from "./AssessmentModal";

interface CustomState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgCustomAssessments = ({ setOrgToSave, setShowToast, setToastText, setToastBody }: CustomState) => {
  const { auth, showMenu }: any = useApp();
  const [errMsg, setErrMsg] = useState("");
  const [showCreateType, setShowCreateType] = useState<boolean>(false);
  const [archivedAssessTypes, setArchivedAssessTypes] = useState<AssessmentType[]>([]);
  const [assessTypes, setAssessTypes] = useState<AssessmentType[]>([]);
  const [defaultTypes, setDefaultTypes] = useState<AssessmentType[]>([]);
  const [filteredAssessTypes, setFilteredAssessTypes] = useState<AssessmentType[]>([]);
  const [answerScales, setAnswerScales] = useState<any[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showPublished, setShowPublished] = useState<boolean>(false);
  const [showDefault, setShowDefault] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewType, setPreviewType] = useState<AssessmentType>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAssessmentTypes(false);
    getAnswerScales();
  }, [auth?.profile]);

  const getAssessmentTypes = (showPub: boolean) => {
    setIsLoading(true);

    fetchAssessmentTypes()
      .then((res: any) => {
        var types = res[0].data;
        types = types.map((t: any) => {
          const questions = t.question.map((q: any) => {
            var question = { ...q, name: q.question };
            return question;
          });

          return { ...t, question: questions };
        });

        const defaultTypes = types.filter((t: any) => t.organization_id === null);
        setDefaultTypes(defaultTypes);
        const archivedTypes = types.filter((t: any) => t.is_archived === 1 && t.organization_id !== null);
        setArchivedAssessTypes(archivedTypes);
        const unArchivedTypes = types.filter((t: any) => t.is_archived !== 1 && t.organization_id !== null);
        setAssessTypes(unArchivedTypes);

        if (showPub) {
          setFilteredAssessTypes(unArchivedTypes.filter((t: any) => t.is_published === 1));
        } else {
          setFilteredAssessTypes(unArchivedTypes.filter((t: any) => t.is_published !== 1));
        }
        setShowPublished(showPub);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setErrMsg(err?.response?.data?.msg);
        setIsLoading(false);
      });
  };

  const getAnswerScales = () => {
    fetchFieldSets()
      .then((res: any) => {
        var sets = res[0].data;

        interface AnswerScale {
          id: number;
          name: string;
          key: number;
          field_id: number;
          value_description: string;
          value: string;
          order: number;
        }

        const group = new Map();
        sets.forEach(({ id, name, key, field_id, value_description, value, order }: AnswerScale) => {
          if (!group.has(id)) {
            group.set(id, { id, name, key, fields: [] });
          }
          group.get(id).fields.push({ field_id, value_description, value, order });
        });

        const finalSet = Array.from(group.values());
        setAnswerScales(finalSet);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickCreateType = () => {
    setErrMsg("");
    setShowCreateType(true);
    setShowArchived(false);
  };

  const onClickCreateTypeCancel = () => {
    setErrMsg("");
    if (unsavedChanges.value) {
      setShowModal(true);
    } else {
      setShowCreateType(false);
      unsavedChanges.value = false;
      setOrgToSave(false);
      getAssessmentTypes(false);
    }
  };

  const onLeaveCreateAssessments = () => {
    setErrMsg("");
    setShowCreateType(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onViewPublished = (publish: boolean) => {
    setShowPublished(publish);
    var filtered;
    if (publish) {
      filtered = assessTypes.filter((f: any) => f.is_published === 1);
    } else {
      filtered = assessTypes.filter((f: any) => f.is_published !== 1);
    }
    setFilteredAssessTypes(filtered);
  };

  const onViewDefault = () => {
    setShowDefault(true);
  };

  const onViewPreview = (type: any) => {
    setPreviewType(type);
    setShowPreview(true);
  };

  const onClickBack = () => {
    setShowArchived(false);
    setShowDefault(false);
    setErrMsg("");
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : showDefault ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row className="mb-0">
            <Col className="col-5">
              <h4 className="app-header-text">Default Assessments</h4>
            </Col>
            <Col className="text-end">
              <Button className="app-primary-color button" onClick={() => onClickBack()}>
                Back
              </Button>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12}>
              {defaultTypes?.length === 0 ? (
                <p className="mt-2">No Custom Assessments</p>
              ) : (
                <Stack gap={3}>
                  {defaultTypes.map((t: AssessmentType) => (
                    <AssessmentCard
                      key={t.id}
                      type={t}
                      setErrMessage={setErrMsg}
                      getAssessments={getAssessmentTypes}
                      answerScales={answerScales}
                      showArchived={showArchived}
                      setShowArchived={setShowArchived}
                      onViewPreview={onViewPreview}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      isDefault={showDefault}
                      isPublished={showPublished}
                    />
                  ))}
                </Stack>
              )}
            </Col>
          </Row>
        </>
      ) : showArchived ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row className="mb-0">
            <Col className="col-5">
              <h4 className="app-header-text">Archived Custom Assessments</h4>
            </Col>
            <Col className="text-end">
              <Button className="app-primary-color button" onClick={() => onClickBack()}>
                Back
              </Button>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12}>
              {archivedAssessTypes?.length === 0 ? (
                <p className="mt-2">No Archived Custom Assessments</p>
              ) : (
                <Stack gap={3}>
                  {archivedAssessTypes.map((t: AssessmentType) => (
                    <AssessmentCard
                      key={t.id}
                      type={t}
                      setErrMessage={setErrMsg}
                      getAssessments={getAssessmentTypes}
                      answerScales={answerScales}
                      showArchived={showArchived}
                      setShowArchived={setShowArchived}
                      onViewPreview={onViewPreview}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      isDefault={false}
                      isPublished={showPublished}
                    />
                  ))}
                </Stack>
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row className="mb-0">
            <Col className="col-5">
              <h4 className="app-header-text">Custom Assessments</h4>
            </Col>
            <Col className="text-end">
              {!showCreateType && (
                <>
                  <Button className="app-secondary-color me-3 button" onClick={() => onViewDefault()}>
                    Default
                  </Button>
                  <Button className="app-secondary-color me-3 button" onClick={() => setShowArchived(true)}>
                    Archived
                  </Button>
                </>
              )}
              <Button className="app-primary-color button" onClick={showCreateType ? onClickCreateTypeCancel : onClickCreateType}>
                {showCreateType ? "Cancel" : "Create Assessment"}
              </Button>
            </Col>
          </Row>
          <Row className="mb-4"></Row>
          {showCreateType ? (
            <CreateAssessmentCard
              getAssessTypes={getAssessmentTypes}
              answerScales={answerScales}
              setOrgToSave={setOrgToSave}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              setShowCreateType={setShowCreateType}
              setIsLoading={setIsLoading}
              setErrMsg={setErrMsg}
            />
          ) : (
            <>
              <Row className="mb-4">
                <Col></Col>
                {!showDefault && (
                  <>
                    <Col className="col-3 d-grid">
                      <Button
                        className={!showPublished ? "app-primary-color button" : "app-primary-color-inverse button"}
                        onClick={(e) => onViewPublished(false)}
                      >
                        <FontAwesomeIcon icon={faEdit} className={"me-2"} />
                        {"Drafts"}
                      </Button>
                    </Col>
                    <Col className="col-3 d-grid">
                      <Button
                        className={showPublished ? "app-primary-color button" : "app-primary-color-inverse button"}
                        onClick={() => onViewPublished(true)}
                      >
                        <FontAwesomeIcon icon={faBullhorn} className={"me-2"} />
                        {"Published"}
                      </Button>
                    </Col>
                  </>
                )}
                <Col></Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12}>
                  {filteredAssessTypes?.length === 0 ? (
                    <p className="mt-2">No Custom Assessments</p>
                  ) : (
                    <Stack gap={3}>
                      {filteredAssessTypes.map((t: AssessmentType) => (
                        <AssessmentCard
                          key={t.id}
                          type={t}
                          setErrMessage={setErrMsg}
                          getAssessments={getAssessmentTypes}
                          answerScales={answerScales}
                          showArchived={showArchived}
                          setShowArchived={setShowArchived}
                          onViewPreview={onViewPreview}
                          setOrgToSave={setOrgToSave}
                          setShowToast={setShowToast}
                          setToastText={setToastText}
                          setToastBody={setToastBody}
                          isDefault={showDefault}
                          isPublished={showPublished}
                        />
                      ))}
                    </Stack>
                  )}
                </Col>
              </Row>
            </>
          )}
          <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onLeaveCreateAssessments} />
        </>
      )}
      <AssessmentModal showModal={showPreview} setShowModal={setShowPreview} assessment={previewType} />
    </>
  );
};

export default React.memo(OrgCustomAssessments);
